import {hot} from 'react-hot-loader/root'; // must come before react
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, BrowserRouter, Route } from "react-router-dom";
import 'react-vis/dist/styles/plot.scss';
import 'react-vis/dist/styles/legends.scss';
import 'react-vis/dist/styles/radial-chart.scss';
import 'react-vis/dist/styles/treemap.scss';

import styles from './App.module.scss'
import { CircularProgress, makeStyles } from '@material-ui/core';
import config from '../_helpers/app.constants';
import { history } from '../_helpers';
import PrivateRoute from '../_helpers/privateRoute'
import MainHeader from "app/components/MainHeader";
import ApiKeyList from 'app/components/ApiKeyList';
import HomePage from 'app/components/HomePage';
import ThrottleComponent from 'app/components/Throttle';
import LogComponent from 'app/components/Logging';
import { RootState } from './rootReducer';
import { ILoggedInUser, removeLoggedInUser, setLoggedInUser } from 'app/reducers/loginUserSlice';
import authenticationService from 'app/services/authentication.service';
import venueService from 'app/services/venue.service';
import { setSelectedVenue, resetSelectedVenue, handleOpenVenueListDrawer, IVenue, setVenues } from 'app/reducers/venueSlice';
import VenueListSelect from 'app/components/VenueListSelect';
import { first } from 'rxjs/operators';
import PartnerOAuthComponent from 'app/components/PartnerOAuthComponent';
import OAuth2LoadingPage from 'app/components/OAuth2LoadingPage';
import appValues from "app/_helpers/app.constants"
import OAuth2Service from 'app/services/OAuth2.service';

console.log(`
------------------------------------------------------

--- APP VERSION - Short hash: ${appValues.VERSION.split("|").join(" Committed: ")}

------------------------------------------------------
`);

const venueListDrawerWidth = 260
const useStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(0, 1),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
    },
    content: {
        flexGrow: 1,
        padding: theme.spacing(3),
    },
    root: {
        display: 'flex',
    },
    venueListDrawer: {
        width: venueListDrawerWidth,
        flexShrink: 0,
    },
    venueListDrawerPaper: {
        width: venueListDrawerWidth,
    },
    venueListDrawerHeader: {
        display: 'flex',
        alignItems: 'centre',
        padding: theme.spacing(0, 1),
        ...theme.mixins.toolbar,
        justifyContent: 'flex-start',
    },
}));

function App() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { loggedInUser } = useSelector((state: RootState) => state.loginUserReducer);
    const { isLoading } = useSelector((state: RootState) => state.appInitReducer)

    const handleUserLogOut = () => {
        authenticationService.logout();
        dispatch(removeLoggedInUser());
        dispatch(resetSelectedVenue());
    }

    const handleVenueListDrawerOpen = () => {
        dispatch(handleOpenVenueListDrawer(true));
      }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const venueId = parseInt(params.get('venueId'));
        const accountId = params.get('accountId');
        const openConnectIntegration = params.get('openConnectIntegration');
        if (openConnectIntegration) {
            localStorage.setItem('openConnectIntegration', openConnectIntegration);
        }

        // we have to set the selectedAccountId before it redirects to OAuth2 login page, so it is there when we come back
        if (params.get('accountId')) {
            localStorage.setItem('selectedAccountId', accountId);
        }
        if (token) {
            //After we logged In - We need to remove the Token query param from the url
            history.push('/connect/');
        }
        authenticationService.currentUser.pipe(first())
            .subscribe((user) =>{
                if (user) {
                    const castedUser = user as ILoggedInUser;
                    dispatch(setLoggedInUser(castedUser));
                    if (openConnectIntegration === 'true') {
                        window.location.replace('/connect/apikeys');
                    }

                    let alreadySelectedAccountId = localStorage.getItem('selectedAccountId');
                    if (alreadySelectedAccountId?.toLowerCase() === 'null') {
                        alreadySelectedAccountId = null;
                    }

                    /**
                     * I don't think the castedUser.accountId is ever populated, but I could be wrong.
                     * This logic was here for awhile and maybe it worked before we added OAuth2. Doesn't
                     * seem to be doing any harm, so I'm leaving it for now.
                     */
                    if (!params.get('accountId') && !alreadySelectedAccountId) {
                        localStorage.setItem('selectedAccountId', castedUser.accountId);
                    } else {
                        localStorage.setItem('selectedAccountId', alreadySelectedAccountId);
                    }

                    // Load the venues for the logged-in user
                    venueService.getAll('-Name')
                        .then((venues: IVenue[]) => {
                            if (venues) {
                                dispatch(setVenues(venues));
                            }
                        });
                    const alreadySelectedVenue = JSON.parse(localStorage.getItem('selectedVenue'));
                    // No Venue is passed in the Query Params - Check if we have a selctedVenue in localStorage

                    if (!params.get('venueId') && alreadySelectedVenue) {
                        dispatch(setSelectedVenue(alreadySelectedVenue));
                    } else {
                        let venuePromise: Promise<IVenue>;
                        if (venueId > 0) {
                            venuePromise = venueService.getById(Number(venueId));
                        } else {
                            venuePromise = venueService.getDefaultVenue();
                        }
                        venuePromise.then((defaultSelectedVenue) => {
                            if (defaultSelectedVenue) {
                                dispatch(setSelectedVenue(defaultSelectedVenue));
                            } else {
                                dispatch(resetSelectedVenue());
                            }
                        });
                    }
                }
            });
        if (config.environment !== 'local' && config.enableOAuth2) {
            const accessToken = OAuth2Service.getOAuth2AccessToken();
            localStorage.setItem('token', accessToken)
            OAuth2Service.periodicalCheckForTokenValidity(accessToken, (newToken) => localStorage.setItem('token', newToken));
        }
    }, [dispatch]);

    return (
          <div className={styles.newApp}>

              <div className={classes.root}>
                  {isLoading ? (
                    <div className={styles.loadingStyle}>
                        <CircularProgress/>
                    </div>
                  ) : null}
                  <BrowserRouter basename="/connect">
                      <MainHeader handleLogOut={handleUserLogOut} handleVenueListDrawerOpen={handleVenueListDrawerOpen}
                                  user={loggedInUser}/>
                      <main className={classes.content}>
                          <div className={classes.toolbar}/>
                          <Switch>
                              <PrivateRoute exact path={["/", "/dashboard", "/dashboard/:page"]} component={HomePage}/>
                              <PrivateRoute exact path={["/apikeys", "/apikeys/:locationId"]} component={ApiKeyList}/>
                              <PrivateRoute path="/throttle" component={ThrottleComponent}/>
                              <PrivateRoute path="/log" component={LogComponent}/>
                              <Route path="/OAuth2" component={OAuth2LoadingPage}/>
                              <PrivateRoute path="/oauth" component={PartnerOAuthComponent}/>
                          </Switch>
                      </main>

                  </BrowserRouter>
                  <VenueListSelect/>
              </div>
          </div>
    );
}

export default hot(App); // needs this on root element for hot module replacement to work
// export default App;
